import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { serviceCategories, INTERACTIVE_ITEMS } from '../data/servicesData';
import ServiceSlideout from './ServiceSlideout';

/**
 * Services Component
 * Displays service categories in a grid with interactive slideout panels.
 * Desktop: hover-triggered slideout panel positioned near the text.
 * Mobile: tap-triggered modal overlay centered in the viewport.
 */
function Services() {
  const [activeSlide, setActiveSlide] = useState(null);
  const [hoverPanel, setHoverPanel] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const [bridgeStyle, setBridgeStyle] = useState({});
  const [mobileModal, setMobileModal] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768
  );
  const triggerRefs = useRef({});
  const closeTimeoutRef = useRef(null);
  const showSlide = activeSlide !== null || hoverPanel;

  // Track viewport size
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // Close desktop panel if switching to mobile
      if (mobile) {
        setActiveSlide(null);
        setHoverPanel(false);
      }
      // Close mobile modal if switching to desktop
      if (!mobile) {
        setMobileModal(null);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile modal on Escape key
  useEffect(() => {
    if (!mobileModal) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setMobileModal(null);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [mobileModal]);

  // Lock body scroll when mobile modal is open
  useEffect(() => {
    if (mobileModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileModal]);

  // Desktop hover handlers
  const handleMouseEnter = (itemName) => {
    if (isMobile) return;
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveSlide(itemName);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    closeTimeoutRef.current = setTimeout(() => {
      setActiveSlide(null);
      setHoverPanel(false);
    }, 150);
  };

  const handlePanelEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setHoverPanel(true);
  };

  // Mobile tap handler
  const handleItemClick = useCallback((itemName) => {
    if (!isMobile) return;
    setMobileModal(itemName);
  }, [isMobile]);

  // Position the desktop panel relative to the hovered item
  useEffect(() => {
    if (activeSlide && triggerRefs.current[activeSlide] && !isMobile) {
      const rect = triggerRefs.current[activeSlide].getBoundingClientRect();
      const panelWidth = Math.min(520, window.innerWidth * 0.9);

      let left = rect.right + 50;
      let panelOnRight = true;

      if (left + panelWidth > window.innerWidth - 20) {
        left = Math.max(20, rect.left - panelWidth - 50);
        panelOnRight = false;
      }

      // Vertically center the panel in the viewport
      const panelTop = window.innerHeight / 2;
      setPanelPosition({ top: panelTop, left: left });

      // Bridge: curved line from text to panel (class-diagram style)
      const textCenterY = rect.top + rect.height / 2;
      const panelCenterY = window.innerHeight / 2;

      if (panelOnRight) {
        setBridgeStyle({
          startX: rect.right + 2,
          startY: textCenterY,
          endX: left - 2,
          endY: panelCenterY,
        });
      } else {
        setBridgeStyle({
          startX: rect.left - 2,
          startY: textCenterY,
          endX: left + panelWidth + 2,
          endY: panelCenterY,
        });
      }
    }
  }, [activeSlide, isMobile]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <section id="services" className="services">
      <div className="section-inner">
        <h2 className="section-title">
          <span className="title-emerald">What we</span>{' '}
          <span className="title-gold">create</span>
        </h2>
        <p className="section-intro">
          We combine industrial durability with artistic elegance. Our resin
          masters hand-pour unique countertops and floors that mimic the world's
          finest stones. Our plaster specialists turn flat walls into textured
          art. From a stunning cinematic media wall to a pristine resin-bound
          driveway, we curate every inch of your space to reflect modern luxury.
          We deliver luxury bespoke solutions for homes and businesses.
        </p>

        {/* Service Cards Grid */}
        <div className="services-grid">
          {serviceCategories.map((group, i) => (
            <div
              key={i}
              className="service-card"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              <h3 className="service-card-title">{group.title}</h3>
              <ul className="service-list">
                {group.items.map((item, j) =>
                  INTERACTIVE_ITEMS.includes(item) ? (
                    <li
                      key={j}
                      role="button"
                      tabIndex={0}
                      aria-expanded={activeSlide === item || mobileModal === item}
                      className={`service-item-interactive ${
                        activeSlide === item || mobileModal === item
                          ? 'active'
                          : ''
                      }`}
                      onMouseEnter={() => handleMouseEnter(item)}
                      onMouseLeave={handleMouseLeave}
                      onClick={() => handleItemClick(item)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleItemClick(item);
                        }
                      }}
                    >
                      <span
                        ref={(el) => (triggerRefs.current[item] = el)}
                        className="service-item-text"
                      >
                        {item}
                      </span>
                      <span className="service-item-hint" aria-hidden="true">ⓘ</span>
                    </li>
                  ) : (
                    <li key={j}>{item}</li>
                  )
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* ===== DESKTOP: hover slideout via portal ===== */}
      {!isMobile &&
        createPortal(
          <>
            {showSlide && activeSlide && bridgeStyle.startX != null && (
              <svg
                className="service-slide-bridge-svg"
                aria-hidden="true"
                onMouseEnter={handlePanelEnter}
                onMouseLeave={handleMouseLeave}
              >
                {/* Invisible wide path for hover detection */}
                <path
                  d={`M ${bridgeStyle.startX} ${bridgeStyle.startY} C ${(bridgeStyle.startX + bridgeStyle.endX) / 2} ${bridgeStyle.startY}, ${(bridgeStyle.startX + bridgeStyle.endX) / 2} ${bridgeStyle.endY}, ${bridgeStyle.endX} ${bridgeStyle.endY}`}
                  fill="none"
                  stroke="transparent"
                  strokeWidth="30"
                  className="service-bridge-hitarea"
                />
                {/* Visible curved line */}
                <path
                  d={`M ${bridgeStyle.startX} ${bridgeStyle.startY} C ${(bridgeStyle.startX + bridgeStyle.endX) / 2} ${bridgeStyle.startY}, ${(bridgeStyle.startX + bridgeStyle.endX) / 2} ${bridgeStyle.endY}, ${bridgeStyle.endX} ${bridgeStyle.endY}`}
                  fill="none"
                  className="service-bridge-line"
                />
                {/* Dot at text side */}
                <circle
                  cx={bridgeStyle.startX}
                  cy={bridgeStyle.startY}
                  r="4"
                  className="service-bridge-dot"
                />
                {/* Dot at panel side */}
                <circle
                  cx={bridgeStyle.endX}
                  cy={bridgeStyle.endY}
                  r="4"
                  className="service-bridge-dot"
                />
              </svg>
            )}

            <div
              role="tooltip"
              aria-label={activeSlide ? `Details about ${activeSlide}` : undefined}
              className={`service-slide-panel ${
                showSlide && activeSlide ? 'active' : ''
              }`}
              style={{ top: panelPosition.top, left: panelPosition.left }}
              onMouseEnter={handlePanelEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div className="service-slide-panel-inner">
                <ServiceSlideout serviceName={activeSlide} />
              </div>
            </div>
          </>,
          document.body
        )}

      {/* ===== MOBILE: tap-triggered modal overlay via portal ===== */}
      {isMobile &&
        mobileModal &&
        createPortal(
          <div
            className="service-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={mobileModal ? `Details about ${mobileModal}` : undefined}
            onClick={(e) => {
              if (e.target === e.currentTarget) setMobileModal(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setMobileModal(null);
            }}
          >
            <div className="service-modal">
              <button
                className="service-modal-close"
                onClick={() => setMobileModal(null)}
                aria-label="Close dialog"
                autoFocus
              >
                ✕
              </button>
              <div className="service-modal-body">
                <ServiceSlideout serviceName={mobileModal} />
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* SEO Hidden Content */}
      <div
        className="seo-hidden-content"
        aria-hidden="true"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: '0',
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: '0',
        }}
      >
        <h2>Interior and Exterior Decorating Services</h2>
        <p>Emerald Horizon provides professional interior decorating and exterior decorating services across Wales, UK, London, Dublin and Ireland. Our decorative walling specialists serve Swansea, Cardiff, Port Talbot, Bridgend, Neath, Llanelli, The Gower, Carmarthen, Pontardawe, Ammanford, Maesteg, Gorseinon, Pyle, Crosshands, Portcawl, Pontardulais, Pembrey, Clydach, Ystradgynlais, Llandudno, Meath, and Wexford.</p>

        <h3>Decorative Walling and Wall Finishes</h3>
        <p>Professional wall panelling Swansea and Cardiff. Venetian plaster Swansea, polished plaster Cardiff, decorative walling Wales. Velvet effect walls, concrete effect walls, gold effect plaster, copper effect plaster. Media walls Swansea, media walls Cardiff. Feature walls Port Talbot, feature walls Bridgend, feature walls Neath.</p>

        <h3>Epoxy Resin Floors and Marble Effect Flooring</h3>
        <p>Epoxy resin floors Swansea, epoxy resin floors Cardiff, epoxy resin floors Wales. Marble effect floors Swansea, marble effect floors Cardiff. Stone carpet Swansea, stone carpet Cardiff, stone carpet Wales. Diamond embedded floors, crystal embedded floors and counters.</p>

        <h3>Kitchen and Bathroom Renovation</h3>
        <p>Kitchen renovation Swansea, kitchen renovation Cardiff. Bathroom renovation Swansea, bathroom renovation Cardiff. Custom counters Swansea, custom counters Cardiff. Vanity units Swansea, vanity units Cardiff, vanity units Wales.</p>

        <h3>Outdoor and Garden Services</h3>
        <p>Resin driveways Swansea, resin driveways Cardiff, resin driveways Wales, resin bound driveways Port Talbot, resin bound driveways Bridgend. Landscaping Swansea, landscaping Cardiff, commercial landscaping Wales. Pergolas Swansea, pergolas Cardiff. Decking Swansea, decking Cardiff, composite decking Wales. Outdoor tiling Swansea, outdoor tiling Cardiff, paving Wales. Outdoor LED lighting Swansea, outdoor LED lighting Cardiff, garden lighting Wales.</p>

        <h3>Playground and Play Area Surfacing</h3>
        <p>Playground surfacing Swansea, playground surfacing Cardiff, rubber crumb surfacing Wales, wet pour playground flooring, safe play area surfaces Port Talbot, Bridgend, Neath, Llanelli.</p>

        <h3>Commercial Interior Services</h3>
        <p>Shopfitting Swansea, shopfitting Cardiff, shopfitting Wales, retail interiors UK. Gym flooring Swansea, gym flooring Cardiff, fitness centre flooring Wales. Salon flooring Swansea, salon flooring Cardiff, spa interiors Wales. Bar interiors Swansea, cafe interiors Cardiff, hospitality fitouts Wales. Medical clinic flooring Swansea, medical clinic flooring Cardiff, healthcare flooring Wales. Warehouse flooring Swansea, warehouse flooring Cardiff, industrial flooring Wales, warehouse epoxy floors UK.</p>

        <h3>Garage and Residential Flooring</h3>
        <p>Garage flooring Swansea, garage flooring Cardiff, garage epoxy floors Wales, residential epoxy flooring Port Talbot, Bridgend, Neath, Llanelli, The Gower, Carmarthen.</p>

        <h3>Custom Office and Brand Interiors</h3>
        <p>Office flooring Swansea, office flooring Cardiff, corporate interiors Wales. Branded environments Swansea, branded environments Cardiff, brand integration interiors UK. Custom floors and walls for offices London, Dublin, Wales.</p>

        <h3>LED Lighting Installation</h3>
        <p>Custom LED installation Swansea, custom LED installation Cardiff, recessed LEDs Wales, LED floor lighting, LED counter lighting, architectural LED lighting UK.</p>

        <h3>Service Areas</h3>
        <p>We provide interior decorating, exterior decorating, and decorative walling services throughout: Swansea, Cardiff, Wales, London, Dublin, Meath, Wexford, Port Talbot, Bridgend, Gorseinon, The Gower, Llanelli, Llandudno, Pyle, Carmarthen, Crosshands, Portcawl, Pontardulais, Pembrey, Maesteg, Neath, Ammanford, Clydach, Pontardawe, Ystradgynlais, and across the UK and Ireland.</p>
      </div>
    </section>
  );
}

export default Services;
